import { apiFetch } from "../api/client";
import { tenantPath } from "../utils/tenantContext";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import API from "../config";

function formatLabel(value) {
  if (!value) return "Unknown";

  return String(value)
    .replaceAll("_", " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatDate(value) {
  if (!value) return "N/A";

  const date = new Date(value);

  return Number.isNaN(date.getTime())
    ? String(value)
    : date.toLocaleString();
}

function riskClass(level) {
  return `risk-level risk-level-${String(level || "low").toLowerCase()}`;
}

function RiskRegisterPage() {
  const [showCreateRisk, setShowCreateRisk] =
    useState(false);
  const [creatingRisk, setCreatingRisk] =
    useState(false);
  const [createRiskError, setCreateRiskError] =
    useState("");

  const [editingRisk, setEditingRisk] =
    useState(null);

  const [savingRisk, setSavingRisk] =
    useState(false);

  const [editRiskError, setEditRiskError] =
    useState("");

  const [riskNotice, setRiskNotice] =
    useState("");

  const [editRiskForm, setEditRiskForm] =
    useState({
      title: "",
      likelihood: 1,
      impact: 1,
      control_effectiveness: 0,
      treatment_strategy: "mitigate",
      treatment_plan: "",
      status: "open",
      owner_name: "",
      owner_team: "",
    });

  const [riskForm, setRiskForm] = useState({
    title: "",
    description: "",
    risk_category: "cybersecurity",
    risk_source: "manual",
    asset_name: "",
    asset_type: "",
    threat_description: "",
    vulnerability_description: "",
    likelihood: 3,
    impact: 3,
    control_effectiveness: 0,
    treatment_strategy: "mitigate",
    treatment_plan: "",
    owner_name: "",
    owner_team: "",
    review_frequency: "quarterly",
  });

  const [summary, setSummary] = useState(null);
  const [risks, setRisks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  const loadRisks = useCallback(async (silent = false) => {
    try {
      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      setError("");

      const [summaryResponse, risksResponse] = await Promise.all([
        apiFetch(tenantPath(`/risk-register/summary`)),
        apiFetch(tenantPath(`/risk-register?limit=100`)),
      ]);

      const summaryData = await summaryResponse.json();
      const risksData = await risksResponse.json();

      if (!summaryResponse.ok) {
        throw new Error(
          summaryData.detail || "Unable to load risk summary."
        );
      }

      if (!risksResponse.ok) {
        throw new Error(
          risksData.detail || "Unable to load risk register."
        );
      }

      setSummary(summaryData);
      setRisks(Array.isArray(risksData) ? risksData : []);
    } catch (loadError) {
      console.error("Risk Register load error:", loadError);
      setError(loadError.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadRisks();
  }, [loadRisks]);

  useEffect(() => {
    const handleTenantChange = () => {
      loadRisks();
    };

    window.addEventListener(
      "wirelesspress-tenant-change",
      handleTenantChange
    );

    return () => {
      window.removeEventListener(
        "wirelesspress-tenant-change",
        handleTenantChange
      );
    };
  }, [loadRisks]);

  const heatMap = useMemo(() => {
    const cells = {};

    risks.forEach((risk) => {
      const key = `${risk.residual_likelihood}-${risk.residual_impact}`;

      if (!cells[key]) {
        cells[key] = [];
      }

      cells[key].push(risk);
    });

    return cells;
  }, [risks]);

  const createRisk = async () => {
    if (!riskForm.title.trim()) {
      setCreateRiskError("Risk title is required.");
      return;
    }

    try {
      setCreatingRisk(true);
      setCreateRiskError("");

      const createRiskPath =
        tenantPath(`/risk-register`);

      const createRiskUrl =
        new URL(
          createRiskPath,
          window.location.origin
        );

      const selectedCompanyId =
        createRiskUrl.searchParams.get(
          "company_id"
        );

      if (!selectedCompanyId) {
        throw new Error(
          "Select a company before creating a risk."
        );
      }

      const response = await apiFetch(
        createRiskPath,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ...riskForm,

            company_id:
              Number(selectedCompanyId),

            title: riskForm.title.trim(),
            description:
              riskForm.description.trim() || null,
            asset_name:
              riskForm.asset_name.trim() || null,
            asset_type:
              riskForm.asset_type.trim() || null,
            threat_description:
              riskForm.threat_description.trim() ||
              null,
            vulnerability_description:
              riskForm.vulnerability_description.trim() ||
              null,
            likelihood: Number(riskForm.likelihood),
            impact: Number(riskForm.impact),
            control_effectiveness: Number(
              riskForm.control_effectiveness
            ),
            treatment_plan:
              riskForm.treatment_plan.trim() || null,
            owner_name:
              riskForm.owner_name.trim() || null,
            owner_team:
              riskForm.owner_team.trim() || null,

          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.detail || "Unable to create risk."
        );
      }

      setShowCreateRisk(false);

      setRiskForm({
        title: "",
        description: "",
        risk_category: "cybersecurity",
        risk_source: "manual",
        asset_name: "",
        asset_type: "",
        threat_description: "",
        vulnerability_description: "",
        likelihood: 3,
        impact: 3,
        control_effectiveness: 0,
        treatment_strategy: "mitigate",
        treatment_plan: "",
        owner_name: "",
        owner_team: "",
        review_frequency: "quarterly",
      });

      await loadRisks(true);
    } catch (riskError) {
      console.error(
        "Risk creation error:",
        riskError
      );

      setCreateRiskError(riskError.message);
    } finally {
      setCreatingRisk(false);
    }
  };

  const openEditRisk = (risk) => {
    setEditRiskError("");
    setRiskNotice("");

    setEditingRisk(risk);

    setEditRiskForm({
      title: risk.title || "",
      likelihood: Number(
        risk.likelihood || 1
      ),
      impact: Number(
        risk.impact || 1
      ),
      control_effectiveness: Number(
        risk.control_effectiveness || 0
      ),
      treatment_strategy:
        risk.treatment_strategy ||
        "mitigate",
      treatment_plan:
        risk.treatment_plan || "",
      status:
        risk.status || "open",
      owner_name:
        risk.owner_name || "",
      owner_team:
        risk.owner_team || "",
    });
  };

  const saveRisk = async () => {
    if (!editingRisk) {
      return;
    }

    if (!editRiskForm.title.trim()) {
      setEditRiskError(
        "Risk title is required."
      );
      return;
    }

    try {
      setSavingRisk(true);
      setEditRiskError("");
      setRiskNotice("");

      const response = await apiFetch(
        tenantPath(
          `/risk-register/${editingRisk.id}`
        ),
        {
          method: "PUT",
          headers: {
            "Content-Type":
              "application/json",
          },
          body: JSON.stringify({
            title:
              editRiskForm.title.trim(),

            likelihood:
              Number(
                editRiskForm.likelihood
              ),

            impact:
              Number(
                editRiskForm.impact
              ),

            control_effectiveness:
              Number(
                editRiskForm
                  .control_effectiveness
              ),

            treatment_strategy:
              editRiskForm
                .treatment_strategy,

            treatment_plan:
              editRiskForm
                .treatment_plan
                .trim() || null,

            status:
              editRiskForm.status,

            owner_name:
              editRiskForm
                .owner_name
                .trim() || null,

            owner_team:
              editRiskForm
                .owner_team
                .trim() || null,
          }),
        }
      );

      const data =
        await response.json();

      if (!response.ok) {
        throw new Error(
          data.detail ||
          "Unable to update risk."
        );
      }

      setEditingRisk(null);

      setRiskNotice(
        `Risk ${
          data.risk_number ||
          editingRisk.risk_number
        } updated successfully.`
      );

      await loadRisks(true);

    } catch (riskError) {
      console.error(
        "Risk update error:",
        riskError
      );

      setEditRiskError(
        riskError.message
      );

    } finally {
      setSavingRisk(false);
    }
  };

  const closeRisk = async (risk) => {
    if (risk.status === "closed") {
      return;
    }

    const confirmed =
      window.confirm(
        `Close ${risk.risk_number}: ${risk.title}?`
      );

    if (!confirmed) {
      return;
    }

    try {
      setRiskNotice("");
      setError("");

      const response =
        await apiFetch(
          tenantPath(
            `/risk-register/${risk.id}`
          ),
          {
            method: "PUT",
            headers: {
              "Content-Type":
                "application/json",
            },
            body: JSON.stringify({
              status: "closed",
            }),
          }
        );

      const data =
        await response.json();

      if (!response.ok) {
        throw new Error(
          data.detail ||
          "Unable to close risk."
        );
      }

      setRiskNotice(
        `Risk ${
          data.risk_number ||
          risk.risk_number
        } closed.`
      );

      await loadRisks(true);

    } catch (closeError) {
      console.error(
        "Risk close error:",
        closeError
      );

      setError(
        closeError.message
      );
    }
  };

  if (loading) {
    return (
      <div className="risk-loading">
        Loading Wireless Press Risk Register...
      </div>
    );
  }

  const metrics = summary?.metrics || {};
  const byLevel = Array.isArray(summary?.by_level)
    ? summary.by_level
    : [];

  return (
    <div className="risk-page">
      <section className="risk-header">
        <div>
          <div className="risk-eyebrow">
            ENTERPRISE RISK MANAGEMENT
          </div>

          <h2>Risk Register & Heat Map</h2>

          <p>
            Track inherent and residual risk, assign ownership,
            prioritize treatments, and connect compliance gaps to
            operational risk.
          </p>
        </div>

        <button
          type="button"
          onClick={() => setShowCreateRisk(true)}
        >
          Create Risk
        </button>

        <button
          type="button"
          onClick={() => loadRisks(true)}
          disabled={refreshing}
        >
          {refreshing ? "Refreshing..." : "Refresh Risks"}
        </button>
      </section>

      {error && (
          <div className="risk-warning">
            Risk operation failed: {error}
          </div>
        )}

        {riskNotice && (
          <div className="risk-notice">
            {riskNotice}
          </div>
        )}

      <section className="risk-metric-grid">
        <article>
          <span>Total Risks</span>
          <strong>{metrics.total_risks || 0}</strong>
        </article>

        <article>
          <span>Open Risks</span>
          <strong>{metrics.open_risks || 0}</strong>
        </article>

        <article className="risk-critical-card">
          <span>Critical Risks</span>
          <strong>{metrics.critical_risks || 0}</strong>
        </article>

        <article className="risk-high-card">
          <span>High Risks</span>
          <strong>{metrics.high_risks || 0}</strong>
        </article>

        <article>
          <span>Average Residual Score</span>
          <strong>
            {Number(
              metrics.average_residual_score || 0
            ).toFixed(2)}
          </strong>
        </article>
      </section>

      <section className="risk-dashboard-grid">
        <article className="risk-panel">
          <div className="risk-panel-heading">
            <div>
              <span>RESIDUAL RISK DISTRIBUTION</span>
              <h3>Risk Levels</h3>
            </div>
          </div>

          <div className="risk-level-list">
            {byLevel.map((item) => (
              <div
                className="risk-level-item"
                key={item.residual_level}
              >
                <span className={riskClass(item.residual_level)}>
                  {formatLabel(item.residual_level)}
                </span>

                <div className="risk-level-bar">
                  <div
                    style={{
                      width: `${Math.min(
                        100,
                        Number(item.count || 0) * 20
                      )}%`,
                    }}
                  />
                </div>

                <strong>{item.count}</strong>
              </div>
            ))}
          </div>
        </article>

        <article className="risk-panel">
          <div className="risk-panel-heading">
            <div>
              <span>5 × 5 MATRIX</span>
              <h3>Residual Risk Heat Map</h3>
            </div>
          </div>

          <div className="risk-heatmap-wrapper">
            <div className="risk-heatmap-y-label">
              Impact
            </div>

            <div className="risk-heatmap">
              {[5, 4, 3, 2, 1].map((impact) =>
                [1, 2, 3, 4, 5].map((likelihood) => {
                  const score = likelihood * impact;
                  const key = `${likelihood}-${impact}`;
                  const cellRisks = heatMap[key] || [];

                  let level = "low";

                  if (score >= 20) {
                    level = "critical";
                  } else if (score >= 12) {
                    level = "high";
                  } else if (score >= 6) {
                    level = "medium";
                  }

                  return (
                    <div
                      className={`risk-heatmap-cell risk-heatmap-${level}`}
                      key={key}
                      title={cellRisks
                        .map((risk) => risk.title)
                        .join(", ")}
                    >
                      <span>{score}</span>

                      {cellRisks.length > 0 && (
                        <strong>{cellRisks.length}</strong>
                      )}
                    </div>
                  );
                })
              )}
            </div>

            <div className="risk-heatmap-x-label">
              Likelihood →
            </div>
          </div>
        </article>
      </section>

      <section className="risk-panel">
        <div className="risk-panel-heading">
          <div>
            <span>ACTIVE RISK INVENTORY</span>
            <h3>Enterprise Risk Register</h3>
          </div>
        </div>

        <div className="risk-table-wrapper">
          <table className="risk-table">
            <thead>
              <tr>
                <th>Risk</th>
                <th>Category</th>
                <th>Inherent</th>
                <th>Control Effectiveness</th>
                <th>Residual</th>
                <th>Treatment</th>
                <th>Status</th>
                <th>Owner</th>
                <th>Updated</th>
                  <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              {risks.length > 0 ? (
                risks.map((risk) => (
                  <tr key={risk.id}>
                    <td>
                      <strong>{risk.risk_number}</strong>
                      <span className="risk-table-title">
                        {risk.title}
                      </span>
                    </td>

                    <td>
                      {formatLabel(risk.risk_category)}
                    </td>

                    <td>
                      <span className={riskClass(risk.inherent_level)}>
                        {risk.inherent_score}
                        {" · "}
                        {formatLabel(risk.inherent_level)}
                      </span>
                    </td>

                    <td>
                      {risk.control_effectiveness}%
                    </td>

                    <td>
                      <span className={riskClass(risk.residual_level)}>
                        {risk.residual_score}
                        {" · "}
                        {formatLabel(risk.residual_level)}
                      </span>
                    </td>

                    <td>
                      {formatLabel(risk.treatment_strategy)}
                    </td>

                    <td>{formatLabel(risk.status)}</td>

                    <td>
                      {risk.owner_name || "Unassigned"}
                      <span className="risk-cell-subtext">
                        {risk.owner_team || ""}
                      </span>
                    </td>

                    <td>
                        {formatDate(
                          risk.updated_at
                        )}
                      </td>

                      <td>
                        <div className="risk-row-actions">
                          <button
                            type="button"
                            onClick={() =>
                              openEditRisk(risk)
                            }
                          >
                            Edit
                          </button>

                          <button
                            type="button"
                            disabled={
                              risk.status ===
                              "closed"
                            }
                            onClick={() =>
                              closeRisk(risk)
                            }
                          >
                            {risk.status ===
                            "closed"
                              ? "Closed"
                              : "Close Risk"}
                          </button>
                        </div>
                      </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="10" className="risk-empty">
                    No risks found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
      {editingRisk && (
          <section className="risk-panel risk-create-panel">
            <div className="risk-panel-heading">
              <div>
                <span>
                  RISK LIFECYCLE
                </span>

                <h3>
                  Edit {
                    editingRisk.risk_number
                  }
                </h3>
              </div>

              <button
                type="button"
                onClick={() => {
                  setEditingRisk(null);
                  setEditRiskError("");
                }}
              >
                Cancel
              </button>
            </div>

            {editRiskError && (
              <div className="risk-warning">
                {editRiskError}
              </div>
            )}

            <div className="risk-create-grid">
              <label>
                Title
                <input
                  value={
                    editRiskForm.title
                  }
                  onChange={(event) =>
                    setEditRiskForm(
                      (current) => ({
                        ...current,
                        title:
                          event.target.value,
                      })
                    )
                  }
                />
              </label>

              <label>
                Status
                <select
                  value={
                    editRiskForm.status
                  }
                  onChange={(event) =>
                    setEditRiskForm(
                      (current) => ({
                        ...current,
                        status:
                          event.target.value,
                      })
                    )
                  }
                >
                  <option value="open">
                    Open
                  </option>

                  <option value="under_review">
                    Under Review
                  </option>

                  <option value="accepted">
                    Accepted
                  </option>

                  <option value="mitigating">
                    Mitigating
                  </option>

                  <option value="transferred">
                    Transferred
                  </option>

                  <option value="avoided">
                    Avoided
                  </option>

                  <option value="closed">
                    Closed
                  </option>
                </select>
              </label>

              <label>
                Likelihood
                <select
                  value={
                    editRiskForm.likelihood
                  }
                  onChange={(event) =>
                    setEditRiskForm(
                      (current) => ({
                        ...current,
                        likelihood:
                          event.target.value,
                      })
                    )
                  }
                >
                  {[1, 2, 3, 4, 5].map(
                    (value) => (
                      <option
                        key={value}
                        value={value}
                      >
                        {value}
                      </option>
                    )
                  )}
                </select>
              </label>

              <label>
                Impact
                <select
                  value={
                    editRiskForm.impact
                  }
                  onChange={(event) =>
                    setEditRiskForm(
                      (current) => ({
                        ...current,
                        impact:
                          event.target.value,
                      })
                    )
                  }
                >
                  {[1, 2, 3, 4, 5].map(
                    (value) => (
                      <option
                        key={value}
                        value={value}
                      >
                        {value}
                      </option>
                    )
                  )}
                </select>
              </label>

              <label>
                Control Effectiveness %

                <input
                  type="number"
                  min="0"
                  max="100"
                  value={
                    editRiskForm
                      .control_effectiveness
                  }
                  onChange={(event) =>
                    setEditRiskForm(
                      (current) => ({
                        ...current,
                        control_effectiveness:
                          event.target.value,
                      })
                    )
                  }
                />
              </label>

              <label>
                Treatment
                <select
                  value={
                    editRiskForm
                      .treatment_strategy
                  }
                  onChange={(event) =>
                    setEditRiskForm(
                      (current) => ({
                        ...current,
                        treatment_strategy:
                          event.target.value,
                      })
                    )
                  }
                >
                  <option value="mitigate">
                    Mitigate
                  </option>
                  <option value="accept">
                    Accept
                  </option>
                  <option value="transfer">
                    Transfer
                  </option>
                  <option value="avoid">
                    Avoid
                  </option>
                </select>
              </label>

              <label>
                Owner
                <input
                  value={
                    editRiskForm.owner_name
                  }
                  onChange={(event) =>
                    setEditRiskForm(
                      (current) => ({
                        ...current,
                        owner_name:
                          event.target.value,
                      })
                    )
                  }
                />
              </label>

              <label>
                Team
                <input
                  value={
                    editRiskForm.owner_team
                  }
                  onChange={(event) =>
                    setEditRiskForm(
                      (current) => ({
                        ...current,
                        owner_team:
                          event.target.value,
                      })
                    )
                  }
                />
              </label>
            </div>

            <label>
              Treatment Plan

              <textarea
                value={
                  editRiskForm
                    .treatment_plan
                }
                onChange={(event) =>
                  setEditRiskForm(
                    (current) => ({
                      ...current,
                      treatment_plan:
                        event.target.value,
                    })
                  )
                }
              />
            </label>

            <button
              type="button"
              onClick={saveRisk}
              disabled={savingRisk}
            >
              {savingRisk
                ? "Saving Risk..."
                : "Save Changes"}
            </button>
          </section>
        )}

        {showCreateRisk && (
        <section className="risk-panel risk-create-panel">
          <div className="risk-panel-heading">
            <div>
              <span>RISK MANAGEMENT</span>
              <h3>Create Risk</h3>
            </div>

            <button
              type="button"
              onClick={() =>
                setShowCreateRisk(false)
              }
            >
              Close
            </button>
          </div>

          {createRiskError && (
            <div className="risk-warning">
              {createRiskError}
            </div>
          )}

          <div className="risk-create-grid">
            <label>
              Title
              <input
                value={riskForm.title}
                onChange={(event) =>
                  setRiskForm((current) => ({
                    ...current,
                    title: event.target.value,
                  }))
                }
              />
            </label>

            <label>
              Category
              <input
                value={riskForm.risk_category}
                onChange={(event) =>
                  setRiskForm((current) => ({
                    ...current,
                    risk_category:
                      event.target.value,
                  }))
                }
              />
            </label>

            <label>
              Asset
              <input
                value={riskForm.asset_name}
                onChange={(event) =>
                  setRiskForm((current) => ({
                    ...current,
                    asset_name: event.target.value,
                  }))
                }
              />
            </label>

            <label>
              Asset Type
              <input
                value={riskForm.asset_type}
                onChange={(event) =>
                  setRiskForm((current) => ({
                    ...current,
                    asset_type: event.target.value,
                  }))
                }
              />
            </label>

            <label>
              Likelihood
              <select
                value={riskForm.likelihood}
                onChange={(event) =>
                  setRiskForm((current) => ({
                    ...current,
                    likelihood: event.target.value,
                  }))
                }
              >
                {[1, 2, 3, 4, 5].map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </label>

            <label>
              Impact
              <select
                value={riskForm.impact}
                onChange={(event) =>
                  setRiskForm((current) => ({
                    ...current,
                    impact: event.target.value,
                  }))
                }
              >
                {[1, 2, 3, 4, 5].map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </label>

            <label>
              Control Effectiveness %
              <input
                type="number"
                min="0"
                max="100"
                value={
                  riskForm.control_effectiveness
                }
                onChange={(event) =>
                  setRiskForm((current) => ({
                    ...current,
                    control_effectiveness:
                      event.target.value,
                  }))
                }
              />
            </label>

            <label>
              Treatment
              <select
                value={riskForm.treatment_strategy}
                onChange={(event) =>
                  setRiskForm((current) => ({
                    ...current,
                    treatment_strategy:
                      event.target.value,
                  }))
                }
              >
                <option value="mitigate">
                  Mitigate
                </option>
                <option value="accept">Accept</option>
                <option value="transfer">
                  Transfer
                </option>
                <option value="avoid">Avoid</option>
              </select>
            </label>

            <label>
              Owner
              <input
                value={riskForm.owner_name}
                onChange={(event) =>
                  setRiskForm((current) => ({
                    ...current,
                    owner_name: event.target.value,
                  }))
                }
              />
            </label>

            <label>
              Team
              <input
                value={riskForm.owner_team}
                onChange={(event) =>
                  setRiskForm((current) => ({
                    ...current,
                    owner_team: event.target.value,
                  }))
                }
              />
            </label>
          </div>

          <label>
            Description
            <textarea
              value={riskForm.description}
              onChange={(event) =>
                setRiskForm((current) => ({
                  ...current,
                  description: event.target.value,
                }))
              }
            />
          </label>

          <label>
            Threat
            <textarea
              value={riskForm.threat_description}
              onChange={(event) =>
                setRiskForm((current) => ({
                  ...current,
                  threat_description:
                    event.target.value,
                }))
              }
            />
          </label>

          <label>
            Vulnerability
            <textarea
              value={
                riskForm.vulnerability_description
              }
              onChange={(event) =>
                setRiskForm((current) => ({
                  ...current,
                  vulnerability_description:
                    event.target.value,
                }))
              }
            />
          </label>

          <label>
            Treatment Plan
            <textarea
              value={riskForm.treatment_plan}
              onChange={(event) =>
                setRiskForm((current) => ({
                  ...current,
                  treatment_plan:
                    event.target.value,
                }))
              }
            />
          </label>

          <button
            type="button"
            onClick={createRisk}
            disabled={creatingRisk}
          >
            {creatingRisk
              ? "Creating Risk..."
              : "Save Risk"}
          </button>
        </section>
      )}

    </div>
  );
}

export default RiskRegisterPage;
